import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiError, API_BASE, getSessionTokenForTest } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast, Toaster } from "sonner";
import { Printer } from "lucide-react";

export default function LubricantLabels() {
  const [lubs, setLubs] = useState([]);
  const [copies, setCopies] = useState(4);
  const [sp] = useSearchParams();
  const idsFilter = useMemo(() => {
    const raw = sp.get("ids");
    return raw ? new Set(raw.split(",").filter(Boolean)) : null;
  }, [sp]);

  useEffect(() => { api.get("/masters/lubricants").then((r) => setLubs(r.data)); }, []);

  const shown = idsFilter ? lubs.filter((l) => idsFilter.has(l.lub_id)) : lubs;

  const printSheet = async () => {
    const tok = getSessionTokenForTest();
    try {
      const params = new URLSearchParams({ copies: String(copies) });
      if (idsFilter) params.set("ids", [...idsFilter].join(","));
      const resp = await fetch(`${API_BASE}/lub_barcodes/labels/print?${params.toString()}`, {
        credentials: "include",
        headers: tok ? { Authorization: `Bearer ${tok}` } : {},
      });
      if (!resp.ok) throw new Error(await resp.text());
      const html = await resp.text();
      const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
      const w = window.open(url, "_blank");
      if (!w) {
        toast.error("Popup blocked. Allow popups for this site.");
        URL.revokeObjectURL(url);
        return;
      }
      setTimeout(() => {
        // Some browsers block programmatic .print() on cross-origin blob
        // popups; we swallow that specific failure because the label PDF
        // is already open in the new tab — the user can Ctrl+P themselves.
        // Anything else is genuinely surprising, so log it.
        try { w.print(); } catch (printErr) { console.warn("label print() blocked by browser:", printErr); }
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      }, 800);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="labels-root">
      <Toaster position="top-center" />
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Lubricant Barcode Labels</h1>
        <p className="text-sm text-slate-600 mt-1">Print, stick on bottles, scan at sale. Codes are stable — reprint anytime.</p>
      </div>

      {idsFilter && (
        <div className="bg-emerald-50 border-2 border-emerald-400 rounded-sm p-3 text-emerald-900 text-sm" data-testid="filtered-hint">
          Showing labels only for the <strong>{shown.length}</strong> SKU{shown.length === 1 ? "" : "s"} you just purchased.
          {" "}
          <a href="/lubricant-labels" className="underline font-bold">Show all labels</a>
        </div>
      )}

      <Card className="p-4 border-2 border-slate-300 rounded-sm">
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Copies per SKU</label>
            <Input type="number" min={1} max={40} value={copies} onChange={(e) => setCopies(Math.max(1, Math.min(40, Number(e.target.value))))} className="h-11 mt-1 border border-slate-300 num w-24" data-testid="copies-input" />
          </div>
          <Button data-testid="print-labels-btn" onClick={printSheet} className="bg-slate-900 hover:bg-slate-800 text-white">
            <Printer size={16} className="mr-1" /> Open print sheet
          </Button>
        </div>
        <p className="text-xs text-slate-500 mt-3">
          Tip: 46×24 mm cells fit standard A4 label sheets (24 per page). Set browser print margins to <b>None</b>.
        </p>
      </Card>

      <Card className="p-3 border-2 border-slate-300 rounded-sm">
        <h3 className="font-bold text-slate-800 mb-2">Preview — Barcodes for {shown.length} SKU{shown.length === 1 ? "" : "s"}</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {shown.map((l) => (
            <div key={l.lub_id} className="border border-slate-300 p-2 rounded-sm bg-white">
              <div className="text-xs font-bold truncate mb-1">{l.name}</div>
              <img
                src={`${API_BASE}/lub_barcodes/${l.lub_id}.svg`}
                alt={l.lub_id}
                className="w-full h-14 object-contain"
                data-testid={`barcode-${l.lub_id}`}
              />
              <div className="flex justify-between text-xs font-mono mt-1">
                <span>VAS-{l.lub_id}</span>
                <span>₹{l.sale_price ?? "—"}</span>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
